import { useRef, useState, type FormEvent } from "react";
import {
  CopyIcon,
  FolderIcon,
  PencilIcon,
  PlayIcon,
  PlusIcon,
} from "lucide-react";
import {
  ExecTemplateEditor,
  type ExecTemplate,
  type ExecVariable,
} from "@/components/prds/exec-template-editor";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { personalStatusOf, useMyCompletion } from "@/components/engagements/my-completion";
import { useToast } from "@/hooks/use-toast";
import { useSWRConfig } from "swr";
import { fetcher, postFetcher } from "@/lib/swr";
import { toastError } from "@/lib/toast";
import type { Engagement } from "@/types/domain";

// 执行入口(WED-191/204):一个「Open agent」按钮弹框,选 Agent × 客户端,
// 确认目录和 prompt 后复制或拉起。prompt 来自 GET /prds/:id/exec-prompt,
// 与 wed skill `query.js prompt` 同源。App / CLI 的链接只预填不发送;本地
// 目录浏览器拿不到,按 App 存本机。ccp 支持仓库定位时改选 Git 仓库,
// 由 ccp 根据 origin 找本机项目;旧客户端仍使用本地目录。ccp 走 ccp://prd/execute,
// 在 ccp 里建 worktree 起 CLI;原生 CLI 走各家 deeplink,在用户默认终端里跑。
// 点「执行」或「Copy prompt」时本人还是待处理就顺手设成处理中(WED-219),
// 并记一条执行进 PRD 时间线(WED-282);只打开弹框预览不记。
// 模型(WED-253)只在客户端 = ccp 时可选,claude / codex 各一份前端写死的列表;
// 选具体模型才在 ccp 链接里带 model,「默认」不带,App / CLI 的 deeplink 不认
// 模型参数所以不带。按 agent 分别记在本浏览器,存的值不在列表里时按「默认」。

type Agent = "claude" | "codex";
type Client = "app" | "ccp" | "cli";

export const AGENTS: { id: Agent; label: string }[] = [
  { id: "claude", label: "Claude" },
  { id: "codex", label: "Codex" },
];
export const CLIENTS: { id: Client; label: string }[] = [
  { id: "app", label: "App" },
  { id: "ccp", label: "ccp" },
  { id: "cli", label: "CLI" },
];

// Radix Select 不允许空串 value,「默认」用哨兵值,拼链接时不带 model
const DEFAULT_MODEL = "default";
export const MODELS: Record<Agent, { id: string; label: string }[]> = {
  claude: [
    { id: DEFAULT_MODEL, label: "默认" },
    { id: "claude-fable-5-1", label: "Fable 5.1" },
    { id: "claude-opus-5-5", label: "Opus 5.5" },
  ],
  codex: [
    { id: DEFAULT_MODEL, label: "默认" },
    { id: "gpt-6-astra", label: "Astra" },
    { id: "gpt-6.1-sol", label: "6.1 Sol" },
  ],
};
const modelKey = (agent: Agent) => `exec-model:${agent}`;
const savedModel = (agent: Agent) => {
  const m = localStorage.getItem(modelKey(agent));
  return m && MODELS[agent].some((o) => o.id === m) ? m : DEFAULT_MODEL;
};
const savedModels = (): Record<Agent, string> => ({
  claude: savedModel("claude"),
  codex: savedModel("codex"),
});

type LaunchArgs = {
  prompt: string;
  template: string;
  dir: string;
  repo?: string;
  prdId: string;
  shortId?: string;
  /** 只有 ccp 认;undefined = 用 ccp 的默认模型 */
  model?: string;
};

// ccp 链接只传执行参数和模板 ID,登录后按 prdId + template 向 wed 拉正文;
// repo 与 cwd 互斥,避免按历史目录执行。短 ID 给 worktree 起名。
const ccpUrl = (
  agent: Agent,
  { template, dir, repo, prdId, shortId, model }: LaunchArgs,
) => {
  const params = new URLSearchParams({ prdId, agent });
  if (shortId) params.set("shortId", shortId);
  if (model) params.set("model", model);
  if (repo) {
    params.set("repo", repo);
  } else {
    params.set("cwd", dir);
  }
  params.set("template", template);
  return `ccp://prd/execute?${params}`;
};

/** Agent × 客户端的链接表;null = 该组合没有 deeplink,选项置灰。 */
const LAUNCH: Record<
  Agent,
  Record<Client, ((a: LaunchArgs) => string) | null>
> = {
  claude: {
    app: ({ prompt, dir }) =>
      `claude://code/new?${new URLSearchParams({ q: prompt, folder: dir })}`,
    ccp: (a) => ccpUrl("claude", a),
    cli: ({ prompt, dir }) =>
      `claude-cli://open?${new URLSearchParams({ q: prompt, cwd: dir })}`,
  },
  codex: {
    app: ({ prompt, dir }) =>
      `codex://threads/new?${new URLSearchParams({ prompt, path: dir })}`,
    ccp: (a) => ccpUrl("codex", a),
    cli: null,
  },
};

const DESCRIPTION: Record<Client, string> = {
  app: "确认后在本机 App 里打开，只预填不发送。",
  ccp: "确认后交给 ccp，在该目录新建 worktree 执行。",
  cli: "确认后在默认终端里起 CLI，只预填不发送。",
};

const AGENT_KEY = "exec-agent";
const CLIENT_KEY = "exec-client";
// WED-191 起存的单一目标,没选过 Agent × 客户端时按它折算
const LEGACY_TARGET: Record<string, [Agent, Client]> = {
  claude: ["claude", "app"],
  "claude-cli": ["claude", "ccp"],
  "codex-app": ["codex", "app"],
  "codex-cli": ["codex", "ccp"],
};
const savedChoice = (): [Agent, Client] => {
  const legacy = LEGACY_TARGET[localStorage.getItem("exec-target") ?? ""];
  const agent = localStorage.getItem(AGENT_KEY);
  const client = localStorage.getItem(CLIENT_KEY);
  return [
    AGENTS.some((a) => a.id === agent)
      ? (agent as Agent)
      : (legacy?.[0] ?? "claude"),
    CLIENTS.some((c) => c.id === client)
      ? (client as Client)
      : (legacy?.[1] ?? "app"),
  ];
};

const dirKey = (appId?: string) => `local-dir:${appId ?? "default"}`;
const savedDir = (appId?: string) =>
  localStorage.getItem(dirKey(appId)) ??
  localStorage.getItem(`codex-app-path:${appId ?? "default"}`) ??
  "";
const repoKey = (appId?: string) => `exec-repo:${appId ?? "default"}`;
// repo 定位先由 Mac ccp 支持;手机链接仍交给 iOS 的 cwd 远程执行流程。
const isMobileBrowser = () =>
  /iPhone|iPad|iPod|Android|Mobile/i.test(navigator.userAgent) ||
  (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

type ExecPrompt = {
  prompt: string;
  template: string;
  templates: { key: string; label: string; custom?: boolean }[];
  variables: ExecVariable[];
  vars: Record<string, string>;
  /** 只有支持 repo 定位的 ccp 发布后才启用;缺省兼容旧服务端。 */
  repositoryLaunch?: { enabled: boolean; repos: string[] };
};
// 自定义模板(WED-206)在 template 参数里是 custom:<id>
const CUSTOM_PREFIX = "custom:";
const fetchPrompt = (prdId: string, template?: string) =>
  fetcher<ExecPrompt>([`/prds/${prdId}/exec-prompt`, { template }]);
const loadPrompt = (prdId: string) =>
  fetchPrompt(prdId).then((r) => r.prompt);

// 模板(WED-205)由服务端给出可选项;记住的模板服务端已不认时退回缺省
const TEMPLATE_KEY = "exec-template";

// 走 <a> 点击而非 location.href:同样交给系统处理 scheme,测试里也可观察
const openExternal = (url: string) => {
  const a = document.createElement("a");
  a.href = url;
  a.click();
};

/** 本地目录对话框:App 页标题行「本地目录」用。 */
const useLocalDirDialog = (appId?: string) => {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");

  const openDialog = () => {
    setDraft(savedDir(appId));
    setOpen(true);
  };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const dir = draft.trim();
    if (!dir) return;
    localStorage.setItem(dirKey(appId), dir);
    setOpen(false);
  };

  const dialog = (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent>
        <form onSubmit={submit} className="grid gap-4">
          <DialogHeader>
            <DialogTitle>本地目录</DialogTitle>
            <DialogDescription>
              Claude / Codex 在这个目录里执行，只保存在本浏览器。Finder
              选中文件夹后按 ⌥⌘C 可复制路径。
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-2">
            <Label htmlFor="local-dir">本地仓库路径</Label>
            <Input
              id="local-dir"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="/Users/me/Projects/repo"
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={!draft.trim()}>
              保存
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );

  return { openDialog, dialog };
};

/** 一行横排单选;disabledHint 返回文案的项置灰,hover 出提示。 */
const ChoiceGroup = <T extends string>({
  name,
  label,
  options,
  value,
  onChange,
  disabledHint,
}: {
  name: string;
  label: string;
  options: { id: T; label: string }[];
  value: T;
  onChange: (v: T) => void;
  disabledHint?: (v: T) => string | null;
}) => (
  <>
    <Label id={`exec-${name}-label`}>{label}</Label>
    <RadioGroup
      aria-labelledby={`exec-${name}-label`}
      className="flex items-center gap-4"
      value={value}
      onValueChange={(v) => onChange(v as T)}
    >
      {options.map((o) => {
        const hint = disabledHint?.(o.id);
        const item = (
          <div key={o.id} className="flex items-center gap-1.5">
            <RadioGroupItem
              value={o.id}
              id={`exec-${name}-${o.id}`}
              disabled={!!hint}
              className="peer"
            />
            <Label htmlFor={`exec-${name}-${o.id}`}>
              {o.label}
            </Label>
          </div>
        );
        return hint ? (
          <Tooltip key={o.id}>
            <TooltipTrigger asChild>{item}</TooltipTrigger>
            <TooltipContent>{hint}</TooltipContent>
          </Tooltip>
        ) : (
          item
        );
      })}
    </RadioGroup>
  </>
);

/** Open agent 对话框:选 Agent × 客户端,展示并可编辑 prompt 与本地目录。 */
const useAgentDialog = (
  prdId: string,
  docId: string,
  appId?: string,
  shortId?: string,
  onLaunch?: () => void,
) => {
  const { toast } = useToast();
  const { mutate } = useSWRConfig();
  const [open, setOpen] = useState(false);
  const [[agent, client], setChoice] = useState(savedChoice);
  const [models, setModels] = useState(savedModels);
  const [dir, setDir] = useState("");
  const [repositoryLaunch, setRepositoryLaunch] = useState<ExecPrompt["repositoryLaunch"]>();
  const [repo, setRepo] = useState("");
  const [prompt, setPrompt] = useState<string | null>(null);
  const [template, setTemplate] = useState<string>();
  const [templates, setTemplates] = useState<ExecPrompt["templates"]>([]);
  const [context, setContext] = useState<Pick<ExecPrompt, "variables" | "vars">>({
    variables: [],
    vars: {},
  });
  // null = 编辑器关;{} = 新建;{ template } = 编辑已有
  const [editing, setEditing] = useState<{ template?: ExecTemplate } | null>(
    null,
  );
  // 只认最后一次请求,快速切模板时旧响应不覆盖新的
  const latest = useRef(0);

  const load = (key?: string) => {
    const req = ++latest.current;
    setPrompt(null);
    const apply = (r: ExecPrompt) => {
      if (req !== latest.current) return;
      setPrompt(r.prompt);
      setTemplate(r.template);
      setTemplates(r.templates);
      setContext({ variables: r.variables, vars: r.vars });
      setRepositoryLaunch(r.repositoryLaunch);
      const repos = r.repositoryLaunch?.repos ?? [];
      const remembered = localStorage.getItem(repoKey(appId)) ?? "";
      setRepo(repos.includes(remembered) ? remembered : repos.length === 1 ? repos[0] : "");
    };
    fetchPrompt(prdId, key)
      .catch((e) => (key ? fetchPrompt(prdId) : Promise.reject(e)))
      .then(apply)
      .catch((e) => {
        if (req !== latest.current) return;
        setOpen(false);
        toastError(toast, "加载 prompt 失败", e);
      });
  };
  const openDialog = () => {
    setOpen(true);
    setDir(savedDir(appId));
    setRepositoryLaunch(undefined);
    setRepo("");
    // 同页多个实例各持一份状态,重开时重读,别的实例选过的模型才同步
    setModels(savedModels());
    load(localStorage.getItem(TEMPLATE_KEY) ?? undefined);
  };
  const chooseTemplate = (key: string) => {
    // Radix Select 在选项未就绪时会回调空串,不当成用户选择
    if (!key || key === template) return;
    localStorage.setItem(TEMPLATE_KEY, key);
    setTemplate(key);
    load(key);
  };
  const editTemplate = async () => {
    const id = template?.slice(CUSTOM_PREFIX.length);
    try {
      const own = await fetcher<ExecTemplate[]>(["/me/exec-templates"]);
      const t = own.find((x) => x.id === id);
      if (t) setEditing({ template: t });
    } catch (e) {
      toastError(toast, "加载模板失败", e);
    }
  };
  // 存完直接选中它;删掉的正是当前模板就退回缺省
  const onTemplateSaved = (t: ExecTemplate) => {
    localStorage.setItem(TEMPLATE_KEY, CUSTOM_PREFIX + t.id);
    load(CUSTOM_PREFIX + t.id);
  };
  const onTemplateDeleted = (id: string) => {
    if (template !== CUSTOM_PREFIX + id) return load(template);
    localStorage.removeItem(TEMPLATE_KEY);
    load();
  };
  // 选了就记住,下次打开沿用
  const choose = (a: Agent, c: Client) => {
    localStorage.setItem(AGENT_KEY, a);
    localStorage.setItem(CLIENT_KEY, c);
    setChoice([a, c]);
  };
  const chooseModel = (m: string) => {
    // 同模板:Radix Select 的空串回调不当成用户选择
    if (!m) return;
    localStorage.setItem(modelKey(agent), m);
    setModels((prev) => ({ ...prev, [agent]: m }));
  };
  const model = models[agent];
  const repositoryMode = client === "ccp" && !!repositoryLaunch?.enabled && !isMobileBrowser();
  const selectedRepo = repositoryMode && repositoryLaunch.repos.includes(repo) ? repo : "";
  const chooseRepo = (value: string) => {
    if (!value || !repositoryLaunch?.repos.includes(value)) return;
    setRepo(value);
    localStorage.setItem(repoKey(appId), value);
  };

  const launch = LAUNCH[agent][client];
  const agentLabel = AGENTS.find((a) => a.id === agent)!.label;
  // 记一次执行进时间线(WED-282):模型只有 ccp 认,「默认」不传;
  // 失败不影响执行,只是少一条记录
  const recordExec = () => {
    const execModel = client === "ccp" && model !== DEFAULT_MODEL ? model : undefined;
    postFetcher(`/prds/${prdId}/exec-log`, {
      arg: { template, agent, client, model: execModel },
    })
      // 时间线与详情按文档 id 取(Detail / PrdTimelineSection 用 prd.id),列表 key 一并刷新;
      // 详情的执行状态(WED-283)随之回到进行态
      .then(() => {
        const paths = [docId, prdId].flatMap((id) => [`/prds/${id}/timeline`, `/prds/${id}`]);
        return mutate((key) => Array.isArray(key) && [...paths, "/prds"].includes(key[0]));
      })
      .catch(() => undefined);
  };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const d = dir.trim();
    if (!launch || !(repositoryMode ? selectedRepo : d) || !template || !prompt?.trim()) return;
    if (repositoryMode) localStorage.setItem(repoKey(appId), selectedRepo);
    else localStorage.setItem(dirKey(appId), d);
    openExternal(
      launch({
        prompt,
        template,
        dir: repositoryMode ? "" : d,
        repo: repositoryMode ? selectedRepo : undefined,
        prdId,
        shortId,
        model: model === DEFAULT_MODEL ? undefined : model,
      }),
    );
    recordExec();
    onLaunch?.();
    setOpen(false);
  };
  const copy = async () => {
    if (!prompt) return;
    try {
      await navigator.clipboard.writeText(prompt);
      toast({ title: "已复制", description: "执行 prompt 已复制到剪贴板" });
      recordExec();
      onLaunch?.();
      setOpen(false);
    } catch (e) {
      toastError(toast, "复制失败", e);
    }
  };

  const dialog = (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-2xl">
        <form onSubmit={submit} className="grid min-w-0 gap-4">
          <DialogHeader>
            <DialogTitle>Open agent</DialogTitle>
            <DialogDescription>
              {launch
                ? repositoryMode
                  ? "ccp 会在本机查找该仓库对应的项目并执行。"
                  : DESCRIPTION[client]
                : `${agentLabel} 没有原生 CLI 的 deeplink，换个客户端或复制 prompt。`}
            </DialogDescription>
          </DialogHeader>
          <TooltipProvider>
            <div className="grid grid-cols-[auto_1fr] items-center gap-x-4 gap-y-3">
              <Label id="exec-template-label">模板</Label>
              <div className="flex items-center gap-1">
                <Select
                  value={template}
                  onValueChange={chooseTemplate}
                  disabled={!templates.length}
                >
                  <SelectTrigger
                    size="sm"
                    className="w-40"
                    aria-labelledby="exec-template-label"
                  >
                    <SelectValue placeholder="加载中…" />
                  </SelectTrigger>
                  <SelectContent>
                    {templates.map((t) => (
                      <SelectItem key={t.key} value={t.key}>
                        {t.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {template?.startsWith(CUSTOM_PREFIX) && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    aria-label="编辑模板"
                    title="编辑模板"
                    onClick={editTemplate}
                  >
                    <PencilIcon />
                  </Button>
                )}
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  aria-label="新建模板"
                  title="新建我的模板"
                  disabled={!context.variables.length}
                  onClick={() => setEditing({})}
                >
                  <PlusIcon />
                </Button>
              </div>
              <ChoiceGroup
                name="agent"
                label="Agent"
                options={AGENTS}
                value={agent}
                onChange={(a) => choose(a, client)}
              />
              <ChoiceGroup
                name="client"
                label="客户端"
                options={CLIENTS}
                value={client}
                onChange={(c) => choose(agent, c)}
                disabledHint={(c) =>
                  LAUNCH[agent][c]
                    ? null
                    : `${agentLabel} 没有原生 CLI 的 deeplink`
                }
              />
              {client === "ccp" && (
                <>
                  <Label id="exec-model-label">模型</Label>
                  <Select value={model} onValueChange={chooseModel}>
                    <SelectTrigger
                      size="sm"
                      className="w-40"
                      aria-labelledby="exec-model-label"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {MODELS[agent].map((m) => (
                        <SelectItem key={m.id} value={m.id}>
                          {m.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </>
              )}
            </div>
          </TooltipProvider>
          {repositoryMode ? (
            <div className="grid gap-2">
              <Label id="launch-repo-label" htmlFor="launch-repo">Git 仓库</Label>
              <Select
                value={selectedRepo}
                onValueChange={chooseRepo}
                disabled={!repositoryLaunch.repos.length || prompt == null}
              >
                <SelectTrigger id="launch-repo" aria-labelledby="launch-repo-label">
                  <SelectValue placeholder="选择 Git 仓库" />
                </SelectTrigger>
                <SelectContent>
                  {repositoryLaunch.repos.map((candidate) => (
                    <SelectItem key={candidate} value={candidate}>{candidate}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!repositoryLaunch.repos.length && (
                <p className="text-sm text-muted-foreground">该 App 暂无关联 PR 仓库。</p>
              )}
            </div>
          ) : (
            <div className="grid gap-2">
              <Label htmlFor="launch-dir">本地仓库路径</Label>
              <Input
                id="launch-dir"
                value={dir}
                onChange={(e) => setDir(e.target.value)}
                placeholder="/Users/me/Projects/repo"
              />
            </div>
          )}
          <div className="grid gap-2">
            <Label htmlFor="launch-prompt">Prompt</Label>
            <Textarea
              id="launch-prompt"
              value={prompt ?? ""}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder={prompt == null ? "加载中…" : undefined}
              disabled={prompt == null}
              className="max-h-[50vh]"
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={!prompt?.trim()}
              onClick={copy}
            >
              <CopyIcon />
              Copy prompt
            </Button>
            <Button
              type="submit"
              disabled={!launch || !(repositoryMode ? selectedRepo : dir.trim()) || !template || !prompt?.trim()}
            >
              <PlayIcon />
              执行
            </Button>
          </DialogFooter>
        </form>
        {/* 嵌套弹窗挂在外层 Content 里、form 外:submit 沿 React 树冒泡,
            放进 form 会连带触发「执行」 */}
        {editing && (
          // 按模板挂载,切换时编辑器状态从头来
          <ExecTemplateEditor
            key={editing.template?.id ?? "new"}
            open
            onOpenChange={(o) => !o && setEditing(null)}
            template={editing.template}
            variables={context.variables}
            vars={context.vars}
            onSaved={onTemplateSaved}
            onDeleted={onTemplateDeleted}
          />
        )}
      </DialogContent>
    </Dialog>
  );

  return { openDialog, dialog };
};

/** App 页标题行的「本地目录」:按 App 存在本浏览器,Open agent 拉起时读取。 */
export const LocalDirButton = ({ appId }: { appId: string }) => {
  const localDir = useLocalDirDialog(appId);
  const dir = savedDir(appId);
  return (
    <>
      <Button
        variant="outline"
        size="sm"
        title={dir || "未设置"}
        onClick={() => localDir.openDialog()}
      >
        <FolderIcon />
        本地目录
      </Button>
      {localDir.dialog}
    </>
  );
};

export const ExecMenu = ({
  prdId,
  docId,
  appId,
  shortId,
  engagement,
  compact = false,
  copyOnly = false,
}: {
  prdId: string;
  /** 详情页的 prd.id(文档或业务视图 id);执行记录后按它刷新时间线,缺省同 prdId */
  docId?: string;
  appId?: string;
  shortId?: string;
  /** 执行对象;本人是执行成员且待处理时,点「执行」/「Copy prompt」设为处理中 */
  engagement?: Engagement;
  compact?: boolean;
  /** 没有单一 App 执行对象时只能复制 prompt */
  copyOnly?: boolean;
}) => {
  const { toast } = useToast();
  const { setMyStatus } = useMyCompletion(prdId);
  const autoStart =
    !!engagement?.canComplete && personalStatusOf(engagement) === "open";
  const agent = useAgentDialog(
    prdId,
    docId ?? prdId,
    appId,
    shortId,
    autoStart ? () => setMyStatus("in_progress") : undefined,
  );

  const copy = async () => {
    try {
      // Safari drops the user-gesture across an await: hand the clipboard a
      // pending blob instead of writing after the fetch resolves.
      if (typeof ClipboardItem !== "undefined" && navigator.clipboard.write) {
        await navigator.clipboard.write([
          new ClipboardItem({
            "text/plain": loadPrompt(prdId).then(
              (t) => new Blob([t], { type: "text/plain" }),
            ),
          }),
        ]);
      } else {
        await navigator.clipboard.writeText(await loadPrompt(prdId));
      }
      toast({ title: "已复制", description: "执行 prompt 已复制到剪贴板" });
    } catch (e) {
      toastError(toast, "复制失败", e);
    }
  };

  if (copyOnly) {
    return (
      <Button variant="outline" size="sm" onClick={copy}>
        Copy as prompt
      </Button>
    );
  }

  return (
    <>
      {compact ? (
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label="Open agent"
          title="Open agent"
          onClick={agent.openDialog}
        >
          <PlayIcon aria-hidden="true" />
        </Button>
      ) : (
        <Button variant="outline" size="sm" onClick={agent.openDialog}>
          <PlayIcon />
          Open agent
        </Button>
      )}
      {agent.dialog}
    </>
  );
};
